// useChangePassword.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../services/api';

const useChangePassword = ({isModal = false }) => {
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};

        if (!formData.oldPassword) {
            newErrors.oldPassword = 'Please enter your old password';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'Please enter your new password';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        }

        if (formData.oldPassword && formData.newPassword && 
            formData.oldPassword === formData.newPassword) {
            newErrors.newPassword = 'New password cannot be the same as old password';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your new password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return false;

        setIsSubmitting(true);
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            if (!userData?.user_id) {
                throw new Error('Invalid user session');
            }

            const response = await api.auth.changePassword({
                id: userData.user_id,
                oldPassword: formData.oldPassword,
                newPassword: formData.newPassword
            });

            const { data } = response;
            
            if (data?.status==='success') {
                setFormData({
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                toast.success('Your password has been successfully updated.');

                if (!isModal) {
                    setTimeout(() => {
                        navigate('/login');
                    }, 2000);
                }
                return true;
            } else {
                toast.error(data?.message || 'Failed to change password');
                return false;
            }
        } catch (error) {
            toast.error(
                error.message === 'Invalid user session'
                    ? 'Please login again'
                    : error?.response?.data?.message || 'An error occurred'
            );
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setErrors({});
    };
    
    return {
        formData,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit,
        resetForm
    };
};

export default useChangePassword;